import axios, { AxiosError } from 'axios';
import _ from 'lodash';
import { AdjustmentResponse, ReservationGuest, tripAvailability, Vehicle } from '../../types/trip.type';
import {
    Trip,
    TripOverview,
    Receipt,
    PaymentRequest,
    AdjustmentPersistRequest,
    AdjustmentPreviewRequest,
    ReceiptSummary,
} from 'guest-data-types-ts-lib';
import { CookieKeys, StorageKeys } from '../../types/keys.type';
import { AuthMethods, AuthUtils, ReservationUtils, SharedUtils, ValidationUtils } from '../../utils';
import { logPerformance } from '../../logging/logPerformance.decorator';
import * as Sentry from '@sentry/browser';
import { Currency, LanguageCodes } from '../../types/preference.type';
import ddLogger, { flowId } from '../../logging/ddLogger';
import { DetailedError, ErrorsPrefix } from '../../types/errors.type';
import { AccessToken } from '../../types/auth.type';
import { Configuration } from '../../configuration';
import { nanoid } from 'nanoid';

// TODO: This must be moved to guest-data-types

export type ExtendedReceiptSummary = ReceiptSummary & {
    security_deposit: number | null | undefined;
};

export type ReceiptSummaryResponse = {
    reservation_id: string;
    reservation_system: string;
    summary: ExtendedReceiptSummary;
    converted_summary?: ExtendedReceiptSummary;
};

export class TripService {
    public static shouldIgnoreError(message: string): boolean {
        if (_.isEmpty(message)) {
            return true;
        }
        const errorsToIgnore = new RegExp(Configuration.ignoreErrorList.join('|'));

        return errorsToIgnore.test(message);
    }

    private static useAccessTokenAuth(): boolean {
        const accessToken = SharedUtils.getDecodedAccessToken();
        const reservationId = SharedUtils.getSessionStorageItem(StorageKeys.RESERVATION_ID);
        return AuthUtils.isTokenValid(accessToken) && !_.isEmpty(reservationId);
    }

    private static getDateTZ(date: Date) {
        let timezone = '';
        try {
            // @ts-ignore
            timezone = date.toString().match(/([-\+][0-9]+)\s/)[1];
            timezone = timezone.substr(0, 3) + ':' + timezone.substr(3);
        } catch (e) {
            ddLogger.error('failed parsing timezone:', { date: date.toString(), error: { kind: e.message } });
        }
        return timezone;
    }

    private readonly url = ValidationUtils.validateAndGetEnvVar('REACT_APP_TRIP_API_URL').concat('v1');
    private readonly urlV2 = ValidationUtils.validateAndGetEnvVar('REACT_APP_TRIP_API_URL').concat('v2');
    private readonly appVersion = ValidationUtils.validateAndGetEnvVar('REACT_APP_VERSION');

    public getHeaders(forceLegacy = false) {
        const authMethod = AuthUtils.getAuthMethod();
        const legacyToken = SharedUtils.getSessionStorageItem(StorageKeys.LEGACY_TOKEN);
        const language = SharedUtils.getCookie(CookieKeys.PREFERRED_LANGUAGE) as any;
        const currency = SharedUtils.getCookie(CookieKeys.PREFERRED_CURRENCY) as any;
        const headers: { [key: string]: any } = {
            'Content-Type': 'application/vnd.api+json',
            'X-Currency': currency || Currency.USD,
            'Accept-Language': language || LanguageCodes.EN_US,
            'X-Flow-Id': flowId,
            'X-Client-Identifier': 'guest-portal',
        };

        if ((legacyToken && authMethod === AuthMethods.AUTH_LEGACY) || forceLegacy) {
            headers['X-Trip-Auth-Legacy-Token'] = legacyToken;
            return headers;
        }

        if (TripService.useAccessTokenAuth() && authMethod === AuthMethods.AUTH_RESERVATION) {
            const token = SharedUtils.getSessionStorageItem(StorageKeys.ACCESS_TOKEN);
            headers.Authorization = `Bearer ${token}`;
            return headers;
        }

        const accessToken = SharedUtils.getSessionStorageItem(StorageKeys.ACCESS_TOKEN);
        let isTokenValid: boolean | string = 'empty';
        let accessTokenSubString = 'empty';
        if (accessToken) {
            const decodedAccessToken = SharedUtils.decodeToken(accessToken) as AccessToken;
            isTokenValid = AuthUtils.isTokenValid(decodedAccessToken);
            accessTokenSubString = accessToken.length > 15 ? accessToken.substring(0, 15) : accessToken;
        }
        let legacyTokenSubString = 'empty';
        if (legacyToken) {
            legacyTokenSubString = legacyToken.length > 15 ? legacyToken.substring(0, 15) : legacyToken;
        }
        let reservationId = SharedUtils.getSessionStorageItem(StorageKeys.RESERVATION_ID);
        reservationId = reservationId ? reservationId : 'empty';

        ddLogger.error(
            `No valid trip api authorization found. authMethod: ${authMethod}, forceLegacy: ${forceLegacy}, legacytoken: ${legacyTokenSubString}, accessToken: ${accessTokenSubString}, isAccessTokenValid: ${isTokenValid}, reservationId: ${reservationId}`
        );
        throw new Error('No valid trip api authorization found.');
    }

    public async getTrip(): Promise<Trip> {
        try {
            const reservationId = SharedUtils.getSessionStorageItem(StorageKeys.RESERVATION_ID);
            const headers = this.getHeaders();

            let endpoint = `trip/${reservationId}`;
            if (headers['X-Trip-Auth-Legacy-Token']) {
                endpoint = 'trip';
            }

            return await this.callGetTrip(headers, endpoint);
        } catch (e) {
            throw this.makeAndCaptureDetailedError('getting trip', e);
        }
    }

    public async getTripByLegacyToken(): Promise<Trip | TripOverview> {
        try {
            const headers = this.getHeaders(true);
            return await this.callGetTrip(headers, 'trip');
        } catch (e) {
            throw this.makeAndCaptureDetailedError('getting trip by legacy token', e);
        }
    }

    @logPerformance((instance, args) => {
        const headers = args[0];
        if (headers['X-Trip-Auth-Legacy-Token']) {
            return `Token ${(headers['X-Trip-Auth-Legacy-Token'] || '').substr(0, 15)}`;
        }
        return `Reservation id ${SharedUtils.getSessionStorageItem(StorageKeys.RESERVATION_ID)}`;
    })
    public async callGetTrip(headers: { [key: string]: string }, endpoint: string): Promise<Trip> {
        const { data } = await axios.get(`${this.urlV2}/${endpoint}`, { headers });
        const response = data.data;

        if (_.isEmpty(response) || _.isEmpty(response.attributes)) {
            throw new Error(`Received empty response from Trip API for endpoint: ${endpoint}`);
        }

        const atNoon = 'T12:00:00';
        const trip = response.attributes;

        if (trip.stay) {
            const currentTimezone = TripService.getDateTZ(new Date());

            trip.stay.check_in_date_time += TripService.getDateTZ(new Date(`${trip.stay.check_in_date}${atNoon}${currentTimezone}`));

            trip.stay.check_out_date_time += TripService.getDateTZ(new Date(`${trip.stay.check_out_date}${atNoon}${currentTimezone}`));
        }

        SharedUtils.setSessionStorageItem(StorageKeys.RESERVATION_ID, trip.reservation_id);
        SharedUtils.setSessionStorageItem(
            StorageKeys.CONFIRMATION_CODE,
            trip.reservation ? trip.reservation.confirmation_code : trip.confirmation_code
        );
        // This prevents us from overwriting an existing legacy token handled in the url if no reservation (ie: idv pending)
        if (trip.reservation && trip.reservation.legacy_token) {
            SharedUtils.setSessionStorageItem(StorageKeys.LEGACY_TOKEN, trip.reservation.legacy_token);
        }

        this.removePayBalanceIfRequired(trip);

        return trip;
    }

    @logPerformance((instance, args) => `reservationId ${args[0]}`)
    public async getReceipt(reservationId: string): Promise<Receipt> {
        const headers = this.getHeaders();
        try {
            const { data } = await axios.get(`${this.url}/trip/${reservationId}/receipts`, { headers });
            return data.data?.attributes;
        } catch (e) {
            throw this.makeAndCaptureDetailedError('getting receipt', e);
        }
    }

    @logPerformance((instance, args) => `reservationId ${args[0]}`)
    public async getReceiptSummary(reservationId: string): Promise<ReceiptSummaryResponse> {
        const headers = this.getHeaders();
        try {
            const { data } = await axios.get(`${this.urlV2}/trip/${reservationId}/receipt-summary`, { headers });
            return data.data?.attributes;
        } catch (e) {
            throw this.makeAndCaptureDetailedError('getting receipt', e);
        }
    }

    @logPerformance((instance, args) => `reservationId ${args[0]}`)
    public async getAdjustmentPreview(reservationId: string, adjustment: AdjustmentPreviewRequest): Promise<AdjustmentResponse> {
        const headers = this.getHeaders();
        try {
            const { data } = await axios.post(`${this.url}/trip/${reservationId}/adjustments/preview`, adjustment, { headers });
            return data.data;
        } catch (e) {
            const isNotImplemented = e.response?.status === 501;

            let sendToSentry = true;
            if (adjustment.name === 'cancellation' && isNotImplemented) {
                sendToSentry = false;
            }
            throw this.makeAndCaptureDetailedError('getting adjustment preview', e, sendToSentry);
        }
    }

    @logPerformance((instance, args) => `reservationId ${args[0]}`)
    public async persistAdjustment(reservationId: string, adjustment: AdjustmentPersistRequest): Promise<AdjustmentResponse> {
        const headers = this.getHeaders();
        const kountSessionId = SharedUtils.getSessionStorageItem(StorageKeys.KOUNT_SESSION_ID);
        const payment = { ...adjustment.payment, sessionId: kountSessionId ?? undefined };

        const newKountSessionId = nanoid(32);
        SharedUtils.setSessionStorageItem(StorageKeys.KOUNT_SESSION_ID, newKountSessionId);
        try {
            const { data } = await axios.post(
                `${this.url}/trip/${reservationId}/adjustments/persist`,
                { ...adjustment, payment },
                { headers }
            );
            return data.data;
        } catch (e) {
            throw this.makeAndCaptureDetailedError('persist adjustment', e);
        }
    }

    @logPerformance((instance, args) => `reservationId ${args[0]}`)
    public async getAvailability(reservationId: string): Promise<tripAvailability> {
        const headers = this.getHeaders();
        try {
            const { data } = await axios.get(`${this.url}/trip/${reservationId}/adjustments/extend-stay-availability`, { headers });
            return data.data;
        } catch (e) {
            throw this.makeAndCaptureDetailedError('getting Availability', e);
        }
    }

    @logPerformance((instance, args) => `reservationId ${args[0]}`)
    public async payBalance(reservationId: string, payment: PaymentRequest): Promise<any> {
        const headers = this.getHeaders();
        const kountSessionId = SharedUtils.getSessionStorageItem(StorageKeys.KOUNT_SESSION_ID);

        const newKountSessionId = nanoid(32);
        SharedUtils.setSessionStorageItem(StorageKeys.KOUNT_SESSION_ID, newKountSessionId);
        try {
            const { data } = await axios.post(
                `${this.url}/trip/${reservationId}/payments/payment`,
                { ...payment, sessionId: kountSessionId ?? undefined },
                { headers }
            );
            return data.data;
        } catch (e) {
            throw this.makeAndCaptureDetailedError('pay balance', e);
        }
    }

    @logPerformance((instance, args) => `reservationId ${args[0]}`)
    public async acceptTermsByReservationId(reservationId: string): Promise<void> {
        const headers = this.getHeaders();

        try {
            await axios.post(`${this.url}/reservation/${reservationId}/required-actions/agree-to-contract`, {}, { headers });
        } catch (e) {
            throw this.makeAndCaptureDetailedError('accepting terms', e);
        }
    }

    @logPerformance((instance, args) => `reservationId ${args[0]}`)
    public async optOutOfVehicleRegistration(reservationId: string): Promise<void> {
        const headers = this.getHeaders();

        try {
            await axios.post(`${this.url}/reservation/${reservationId}/required-actions/opt-out-vehicle`, {}, { headers });
        } catch (e) {
            throw this.makeAndCaptureDetailedError('opting-out vehicles', e);
        }
    }

    @logPerformance((instance, args) => `reservationId ${args[0]}`)
    public async updateVehicles(reservationId: string, vehicles: Vehicle[]): Promise<void> {
        const headers = this.getHeaders();

        try {
            await axios.post(`${this.url}/trip/${reservationId}/vehicles`, { data: vehicles }, { headers });
        } catch (e) {
            throw this.makeAndCaptureDetailedError('updating vehicle', e);
        }
    }

    @logPerformance()
    public async addGuest(guest: ReservationGuest): Promise<void> {
        const headers = this.getHeaders();
        try {
            await axios.post(`${this.url}/trip/reservation-guests`, { data: guest }, { headers });
        } catch (e) {
            throw this.makeAndCaptureDetailedError('adding guest', e);
        }
    }

    @logPerformance((instance, args) => `guestId ${args[0]}`)
    public async patchGuest(guestId: number, guest: ReservationGuest): Promise<void> {
        const headers = this.getHeaders();
        try {
            await axios.patch(`${this.url}/trip/reservation-guests/${guestId}`, { data: guest }, { headers });
        } catch (e) {
            throw this.makeAndCaptureDetailedError('patching guest', e);
        }
    }

    @logPerformance((instance, args) => `guestId ${args[0]}`)
    public async deleteGuest(guestId: number): Promise<void> {
        const headers = this.getHeaders();
        try {
            await axios.delete(`${this.url}/trip/reservation-guests/${guestId}`, { headers });
        } catch (e) {
            throw this.makeAndCaptureDetailedError('deleting guest', e);
        }
    }

    @logPerformance((instance, args) => `reservationId ${args[0]}`)
    public async notifyEarlyCheckout(reservationId: string): Promise<void> {
        const headers = this.getHeaders();
        try {
            await axios.post(`${this.url}/trip/${reservationId}/guest-checkout`, {}, { headers });
        } catch (e) {
            throw this.makeAndCaptureDetailedError('notify early checkout', e);
        }
    }

    @logPerformance((instance, args) => `reservationId ${SharedUtils.getSessionStorageItem(StorageKeys.RESERVATION_ID)}`)
    public async cleanReservationCache(): Promise<void> {
        const headers = this.getHeaders();
        try {
            const reservationId = SharedUtils.getSessionStorageItem(StorageKeys.RESERVATION_ID);
            if (reservationId) {
                await axios.post(`${this.url}/trip/${reservationId}/invalidate-cache`, {}, { headers });
            }
        } catch (e) {
            throw this.makeAndCaptureDetailedError('cleaning cache', e);
        }
    }

    public async isPoolHeatAvailable(): Promise<boolean> {
        let reservationId;
        let legacyToken;

        try {
            reservationId = SharedUtils.getSessionStorageItem(StorageKeys.RESERVATION_ID);
            legacyToken = SharedUtils.getSessionStorageItem(StorageKeys.LEGACY_TOKEN);
            if (_.isEmpty(legacyToken)) {
                return false;
            }
            await this.getTripByLegacyToken();
        } catch (e) {
            ddLogger.error(`ERROR - PoolHeat: validating legacy token for resId ${reservationId}`, { error: { kind: e.message } });
            return false;
        }
        try {
            const url = `https://www.${ValidationUtils.getDomain()}/trip?token=${legacyToken}&location=home`;
            const response = await fetch(url, { redirect: 'follow', mode: 'cors', credentials: 'include' });
            const body = await response.text();
            const currentLanguage = this.getHeaders()['Accept-Language'];
            SharedUtils.setCookie(CookieKeys.PREFERRED_LANGUAGE, currentLanguage, { domain: `${ValidationUtils.getCookieDomain()}` });
            return body.indexOf('div id="trip-add-ons"') > 0;
        } catch (e) {
            ddLogger.error(`ERROR - PoolHeat: determining if is available for resId ${reservationId}`, { error: { kind: e.message } });
            return false;
        }
    }

    private removePayBalanceIfRequired(trip: Trip): void {
        if (ReservationUtils.shouldIgnoreBalance(trip?.reservation)) {
            delete trip?.required_actions?.pay_balance;
        }
    }

    private makeAndCaptureDetailedError(tag: string, e: AxiosError, reportToSentry = true): DetailedError {
        let message: string;
        let status = 0;
        let code: string | number = e.code || status;
        let prefix: string = '';
        const { name, stack } = e;

        if (e.response) {
            const { statusText, data } = e.response;
            status = e.response.status;
            code = status;
            const messages = _.map(data.errors, (err) => {
                code = err.code ? err.code : err.status;
                prefix = TripService.getErrorPrefix(code);
                return `${tag} STATUS ${err.status || status} - ${err.code || err.title} ${err.detail ? `- DETAIL: ${err.detail}` : ''}`;
            });

            message = messages.length > 0 ? messages.join(',') : `${tag} STATUS ${status} - ${statusText}`;
        } else {
            message = `${tag} STATUS ${status} - ${e.message}`;
        }

        const exception: DetailedError = { name, message, status, stack, code, errorPrefix: prefix, app_version: this.appVersion };

        if (!TripService.shouldIgnoreError(message) || (stack && !TripService.shouldIgnoreError(stack))) {
            reportToSentry && Sentry.captureException(new Error(exception.message));
        }

        return exception;
    }

    static getErrorPrefix(code: string | number): string {
        let errorPrefix: string = ErrorsPrefix.TRIP_API_ERROR;
        // @ts-ignore
        if (ErrorsPrefix[code]) {
            // @ts-ignore
            errorPrefix = ErrorsPrefix[code];
        }
        if (code == 0) {
            errorPrefix = ErrorsPrefix.NETWORK_ERROR;
        }
        return errorPrefix;
    }
}

export default new TripService();
